/**
 * Lista gare e apertura di una gara nuova.
 *
 * Una gara nasce in **Bozza** sul capitolato attivo di una tipologia, con gli invitati proposti
 * dall'albo (più il fornitore del contratto uscente, se lo si indica). Le gare aperte in
 * automatico dal promemoria disdette compaiono qui con origine «Promemoria disdetta».
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@shared/context/AuthContext";
import { useToast } from "@shared/components/ui/Toast";
import {
  gareApi,
  capitolatiApi,
  contrattiApi,
  anagraficheApi,
} from "../services/acquistiApi";
import {
  Campo,
  Testo,
  Data,
  Scelta,
  Caricamento,
  Vuoto,
  Avviso,
} from "../components/campi";
import {
  STATO_GARA,
  STATO_GARA_BADGE,
  ORIGINE_GARA,
  opzioni,
} from "../constants/optionSet";
import { dataIt, etichetta } from "../utils/formato";

const VUOTA = {
  nome: "",
  capitolatoId: "",
  contrattoUscenteId: "",
  scadenzaOfferte: "",
  rsaIds: [],
};

export default function GarePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { showToast } = useToast();
  const { canPerformAction } = useAuth();
  const puoScrivere = canPerformAction("gare.gestisci");

  const [filtri, setFiltri] = useState({ search: "", stato: "" });
  const [gare, setGare] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState(null);

  const [creazione, setCreazione] = useState(null);
  const [capitolati, setCapitolati] = useState([]);
  const [contratti, setContratti] = useState([]);
  const [rsa, setRsa] = useState([]);
  const [proposta, setProposta] = useState(null);
  const [invio, setInvio] = useState(false);

  const carica = useCallback(async () => {
    setCaricamento(true);
    setErrore(null);
    try {
      const res = await gareApi.list(filtri);
      setGare(res.value || []);
    } catch (e) {
      setErrore(e.message || "Impossibile caricare le gare");
    } finally {
      setCaricamento(false);
    }
  }, [filtri]);

  useEffect(() => {
    const t = setTimeout(carica, 300);
    return () => clearTimeout(t);
  }, [carica]);

  // «Apri gara» dal dettaglio contratto: /gare?contratto=<id>
  useEffect(() => {
    const contratto = params.get("contratto");
    if (contratto && puoScrivere)
      apriCreazione({ contrattoUscenteId: contratto });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function apriCreazione(iniziale = {}) {
    setCreazione({ ...VUOTA, ...iniziale });
    try {
      const [c, k, r] = await Promise.all([
        capitolatiApi.list({ soloAttivi: "true" }),
        contrattiApi.list({}),
        anagraficheApi.rsa(),
      ]);
      setCapitolati(c.value || []);
      setContratti(k.value || []);
      setRsa(r.value || []);
    } catch (e) {
      setErrore(e.message);
    }
  }

  const capitolato = useMemo(
    () => capitolati.find((c) => c.id === creazione?.capitolatoId),
    [capitolati, creazione?.capitolatoId],
  );

  // Anteprima degli invitati: cambia con il capitolato (le sue tipologie) e il contratto
  // uscente, non a ogni tasto battuto nel nome.
  const aperta = !!creazione;
  const capitolatoAnteprima = capitolato?.id;
  const uscenteAnteprima = creazione?.contrattoUscenteId;
  useEffect(() => {
    if (!aperta || (!capitolatoAnteprima && !uscenteAnteprima)) {
      setProposta(null);
      return;
    }
    gareApi
      .propostaInvitati({
        capitolatoId: capitolatoAnteprima,
        contrattoUscenteId: uscenteAnteprima,
      })
      .then((r) => setProposta(r.value || []))
      .catch(() => setProposta(null));
  }, [aperta, capitolatoAnteprima, uscenteAnteprima]);

  const propostiInvitati = (proposta || []).filter(
    (p) => p.motivo !== "albo-parziale",
  );
  const parziali = (proposta || []).filter((p) => p.motivo === "albo-parziale");

  const setC = (k) => (v) => setCreazione((c) => ({ ...c, [k]: v }));

  function scegliContratto(id) {
    const k = contratti.find((x) => x.id === id);
    setCreazione((c) => ({
      ...c,
      contrattoUscenteId: id,
      nome: c.nome || (k ? `Rinnovo ${k.nome}` : ""),
      rsaIds: c.rsaIds.length ? c.rsaIds : (k?.rsaCoperte || []).map((r) => r.id),
    }));
  }

  const toggleRsa = (id) =>
    setCreazione((c) => ({
      ...c,
      rsaIds: c.rsaIds.includes(id)
        ? c.rsaIds.filter((x) => x !== id)
        : [...c.rsaIds, id],
    }));

  const crea = async (e) => {
    e.preventDefault();
    setInvio(true);
    try {
      const res = await gareApi.create(creazione);
      showToast(`Gara ${res.codice} creata in bozza`, "success");
      navigate(`/gare/${res.id}`);
    } catch (err) {
      setErrore(err.data?.message || err.message);
    } finally {
      setInvio(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">Gare</h1>
        <span className="text-sm text-gray-500">
          {caricamento ? "…" : `${gare.length} gare`}
        </span>
        {puoScrivere && (
          <button
            type="button"
            className="btn-primary ml-auto"
            onClick={() => (creazione ? setCreazione(null) : apriCreazione())}
          >
            {creazione ? "Annulla" : "+ Nuova gara"}
          </button>
        )}
      </div>

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      {creazione && (
        <form onSubmit={crea} className="card space-y-4">
          <h2 className="font-semibold text-gray-900">Nuova gara</h2>
          <p className="text-sm text-gray-600">
            Nasce in <strong>Bozza</strong>: fino all'invio si cambia tutto.
            All'invio il capitolato si blocca e i fornitori invitati passano a
            «In trattativa», cioè diventano anonimi nel portale manutenzioni.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Campo
              label="Contratto uscente"
              aiuto="Facoltativo. Se c'è, il fornitore uscente viene invitato e le RSA si precompilano."
            >
              <Scelta
                valore={creazione.contrattoUscenteId}
                onChange={scegliContratto}
                opzioni={contratti.map((k) => ({
                  value: k.id,
                  label: `${k.nome}${k.fornitore?.nome ? ` — ${k.fornitore.nome}` : ""}`,
                }))}
                vuoto="nessuno"
              />
            </Campo>
            <Campo
              label="Capitolato *"
              aiuto="Solo i capitolati attivi: la gara si fa sulla versione in vigore del capitolato, e gli invitati si propongono su tutti i suoi servizi."
            >
              <Scelta
                valore={creazione.capitolatoId}
                onChange={setC("capitolatoId")}
                opzioni={capitolati.map((c) => ({
                  value: c.id,
                  label: `${c.nome} — ${(c.tipologieCoperte || []).map((t) => t.nome).join(", ") || c.tipologiaFornitura?.nome || "?"}`,
                }))}
                required
              />
            </Campo>
            <Campo label="Nome della gara *">
              <Testo valore={creazione.nome} onChange={setC("nome")} required />
            </Campo>
            <Campo
              label="Scadenza offerte"
              aiuto="Serve per inviare; in bozza si può lasciare vuota."
            >
              <Data
                valore={creazione.scadenzaOfferte}
                onChange={setC("scadenzaOfferte")}
              />
            </Campo>
          </div>

          <div>
            <span className="label">RSA interessate</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
              {rsa.map((r) => (
                <label
                  key={r.id}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-acquisti-600 focus:ring-acquisti-500"
                    checked={creazione.rsaIds.includes(r.id)}
                    onChange={() => toggleRsa(r.id)}
                  />
                  {r.nome}
                </label>
              ))}
            </div>
          </div>

          {proposta && (
            <div className="text-sm">
              <span className="label">Invitati proposti</span>
              {propostiInvitati.length === 0 ? (
                <p className="text-gray-500">
                  Nessun fornitore nell'albo per tutte le tipologie del
                  capitolato. Si aggiungono a mano dopo la creazione, o si
                  censiscono nell'Albo.
                </p>
              ) : (
                <ul className="list-disc ml-5">
                  {propostiInvitati.map((p) => (
                    <li key={p.fornitoreId}>
                      {p.nome}{" "}
                      <span className="text-gray-500">
                        ({p.motivo === "uscente" ? "uscente" : "albo"}
                        {p.email ? `, ${p.email}` : ", senza email"})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {parziali.length > 0 && (
                <div className="mt-2">
                  <span className="label">
                    Coprono solo una parte dei servizi (non invitati in
                    automatico)
                  </span>
                  <ul className="list-disc ml-5">
                    {parziali.map((p) => (
                      <li key={p.fornitoreId}>
                        {p.nome}{" "}
                        <span className="text-gray-500">
                          (manca: {p.mancanti.join(", ")})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <button type="submit" className="btn-primary" disabled={invio}>
            {invio ? "Creazione…" : "Crea in bozza"}
          </button>
        </form>
      )}

      <div className="card !p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
          <Campo label="Cerca">
            <Testo
              valore={filtri.search}
              onChange={(v) => setFiltri((f) => ({ ...f, search: v }))}
              placeholder="nome o codice"
            />
          </Campo>
          <Campo label="Stato">
            <Scelta
              valore={filtri.stato}
              onChange={(v) => setFiltri((f) => ({ ...f, stato: v }))}
              opzioni={opzioni(STATO_GARA)}
              vuoto="tutti"
            />
          </Campo>
        </div>
      </div>

      <div className="card !p-0 overflow-x-auto">
        {caricamento ? (
          <Caricamento />
        ) : gare.length === 0 ? (
          <Vuoto titolo="Nessuna gara">
            Una gara chiede un'offerta a più fornitori sul capitolato attivo di
            una tipologia.
          </Vuoto>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Codice</th>
                <th>Gara</th>
                <th>Tipologia</th>
                <th>Stato</th>
                <th>Scadenza offerte</th>
                <th>Origine</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {gare.map((g) => (
                <tr key={g.id}>
                  <td className="whitespace-nowrap font-mono text-xs">
                    {g.codice}
                  </td>
                  <td>
                    <Link
                      to={`/gare/${g.id}`}
                      className="text-acquisti-700 hover:underline font-medium"
                    >
                      {g.nome}
                    </Link>
                  </td>
                  <td>{g.tipologiaFornitura?.nome || "—"}</td>
                  <td>
                    <span className={STATO_GARA_BADGE[g.stato] || "badge-gray"}>
                      {g.statoLabel || etichetta(STATO_GARA, g.stato)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap">
                    {dataIt(g.scadenzaOfferte)}
                  </td>
                  <td className="text-sm text-gray-600">
                    {etichetta(ORIGINE_GARA, g.origine)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
